import { Link } from "react-router-dom";
import { Sun, Wind, Leaf, Droplets, Recycle, TreePine, Wheat, ArrowRight, Zap } from "lucide-react";

const sections = [
  {
    id: "renewable",
    title: "Renewable Energy",
    icon: Sun,
    color: "amber",
    projects: [
      { name: "Solar Power Plants", desc: "Utility-scale and rooftop solar installations for clean energy generation.", who: "Industries, commercial establishments, government bodies" },
      { name: "Wind Energy Projects", desc: "Onshore and offshore wind farm development and operation.", who: "Large landholders, state agencies, IPPs" },
    ]
  },
  {
    id: "bio-carbon",
    title: "Bio & Carbon Projects",
    icon: Leaf,
    color: "emerald",
    projects: [
      { name: "Biogas Plants", desc: "Convert organic waste into biogas for energy and organic manure.", who: "Agri-businesses, dairy farms, food processors" },
      { name: "Biochar Units", desc: "Produce biochar from biomass for soil enhancement and carbon sequestration.", who: "Agricultural enterprises, carbon offset buyers" },
      { name: "Waste-to-Energy Plants", desc: "Convert municipal and industrial waste into electricity.", who: "Municipal corporations, industrial zones" },
      { name: "Algae-based Carbon Capture", desc: "Biological carbon capture using algae cultivation systems.", who: "R&D institutions, coastal industries" },
      { name: "Direct Air Capture (DAC)", desc: "Technology-driven CO2 removal directly from atmosphere.", who: "Tech companies, carbon offset purchasers" },
    ]
  },
  {
    id: "water",
    title: "Water & Treatment",
    icon: Droplets,
    color: "blue",
    projects: [
      { name: "ETP (Effluent Treatment Plant)", desc: "Treat industrial effluents before discharge to meet CPCB standards.", who: "Manufacturing units, chemical plants, textile industries" },
      { name: "STP (Sewage Treatment Plant)", desc: "Treat domestic sewage for safe disposal or reuse.", who: "Residential complexes, townships, municipalities" },
      { name: "WTP (Water Treatment Plant)", desc: "Purify raw water for drinking and industrial use.", who: "Municipalities, industries, institutions" },
      { name: "Zero Liquid Discharge Systems", desc: "Achieve 100% water recovery with zero effluent discharge.", who: "High water-use industries, pharma, textiles" },
    ]
  },
  {
    id: "waste",
    title: "Waste Infrastructure",
    icon: Recycle,
    color: "orange",
    projects: [
      { name: "Plastic Recycling Plants", desc: "Process and recycle plastic waste into reusable materials.", who: "Waste management companies, municipalities, CSR programs" },
      { name: "Solid Waste Processing Units", desc: "Segregate, process, and dispose solid waste scientifically.", who: "Urban local bodies, industrial zones" },
      { name: "Biomedical Waste Facilities", desc: "Safe treatment and disposal of healthcare waste.", who: "Hospitals, diagnostic centers, pharma companies" },
      { name: "Industrial Recycling Systems", desc: "Recover and reuse industrial materials and by-products.", who: "Manufacturing units, heavy industries" },
    ]
  },
  {
    id: "nature",
    title: "Nature-Based Projects",
    icon: TreePine,
    color: "green",
    projects: [
      { name: "Afforestation Projects", desc: "Large-scale tree planting for carbon sequestration and biodiversity.", who: "Corporates (CSR), governments, NGOs" },
      { name: "Corporate Plantation Programs", desc: "Structured plantation drives linked to carbon offset certification.", who: "Corporates seeking carbon neutrality" },
      { name: "Wildlife & Biodiversity Projects", desc: "Habitat restoration and conservation initiatives.", who: "Conservation bodies, CSR programs, governments" },
    ]
  },
  {
    id: "agriculture",
    title: "Agriculture",
    icon: Wheat,
    color: "lime",
    projects: [
      { name: "Large-scale Organic Farming", desc: "Transition conventional farmland to certified organic for carbon offset credits.", who: "FPOs, agri-corporates, state agriculture bodies" },
    ]
  },
];

const colorMap = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "bg-amber-100 text-amber-600", badge: "bg-amber-100 text-amber-700" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "bg-emerald-100 text-emerald-600", badge: "bg-emerald-100 text-emerald-700" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "bg-blue-100 text-blue-600", badge: "bg-blue-100 text-blue-700" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "bg-orange-100 text-orange-600", badge: "bg-orange-100 text-orange-700" },
  green: { bg: "bg-green-50", border: "border-green-200", icon: "bg-green-100 text-green-600", badge: "bg-green-100 text-green-700" },
  lime: { bg: "bg-lime-50", border: "border-lime-200", icon: "bg-lime-100 text-lime-600", badge: "bg-lime-100 text-lime-700" },
};

export default function ClimateProjectsPage() {
  return (
    <div className="pt-20" data-testid="climate-projects-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
              <Zap className="h-4 w-4" /> Build the Future
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Climate Projects & Infrastructure Development
            </h1>
            <p className="mt-6 text-xl text-slate-400 leading-relaxed">
              From renewable energy to waste management — we develop and execute climate infrastructure projects that generate both impact and returns.
            </p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-all">
              Start Your Project <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Project Sections */}
      {sections.map((section) => {
        const colors = colorMap[section.color];
        const Icon = section.icon;
        return (
          <section key={section.id} className={`py-16 ${section.id === 'bio-carbon' || section.id === 'waste' || section.id === 'agriculture' ? 'bg-slate-50' : 'bg-white'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{section.title}</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.projects.map((proj, i) => (
                  <div key={i} className={`${colors.bg} border ${colors.border} rounded-xl p-6 hover:shadow-lg transition-all`}>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{proj.name}</h3>
                    <p className="text-slate-600 text-sm mb-4">{proj.desc}</p>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full ${colors.badge} text-xs font-medium mb-4`}>
                      For: {proj.who}
                    </div>
                    <Link to="/contact" className="block mt-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                      Start Your Project <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Have a Project in Mind?</h2>
          <p className="text-blue-100 text-lg mb-8">We develop, execute, and manage climate infrastructure projects from concept to commissioning.</p>
          <Link to="/contact" className="px-8 py-3 bg-white text-blue-700 font-bold rounded-lg hover:bg-blue-50 transition-all inline-flex items-center gap-2">
            Start Your Project <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
